// Generator API client — public endpoints (search/detail) + authenticated (claim)

import { API } from "./api";

// ─── Types ───────────────────────────────────────────────────────────

export interface GeneratorSummary {
  plant_id: string;
  generator_id: string;
  plant_name: string | null;
  entity_name: string | null;
  technology: string | null;
  energy_source_desc: string | null;
  nameplate_capacity_mw: number | null;
  net_summer_capacity_mw: number | null;
  latitude: number | null;
  longitude: number | null;
  state_id: string | null;
  state_name: string | null;
  county: string | null;
  ba_code: string | null;
  ba_name: string | null;
  status: string | null;
  operating_year_month: string | null;
  sector_name: string | null;
}

export interface GeneratorSummaryStats {
  total_capacity_mw: number;
  count_by_status: Record<string, number>;
  count_by_technology: Record<string, number>;
}

export interface GeneratorSearchResponse {
  generators: GeneratorSummary[];
  total: number;
  page: number;
  page_size: number;
  summary: GeneratorSummaryStats | null;
}

export interface GeneratorDetail extends GeneratorSummary {
  energy_source_code: string | null;
  prime_mover_code: string | null;
  net_winter_capacity_mw: number | null;
  sector: string | null;
  planned_retirement_year_month: string | null;
  current_carbon_intensity: number | null;
}

export interface GeneratorClaimResponse {
  project_id: string;
  project_name: string;
  message: string;
}

export interface GeneratorSearchParams {
  q?: string;
  ba_code?: string;
  state?: string;
  technology?: string;
  status?: string;
  min_capacity?: number;
  max_capacity?: number;
  page?: number;
  page_size?: number;
}

export interface StatePipelineSummary {
  state_id: string;
  state_name: string;
  planned_count: number;
  planned_capacity_mw: number;
  planned_by_technology: Record<string, number>;
  operating_count: number;
  operating_capacity_mw: number;
}

// ─── Status Labels ──────────────────────────────────────────────────

export const STATUS_LABELS: Record<string, string> = {
  OP: "Operating",
  SB: "Standby",
  P: "Planned",
  L: "Regulatory Approved",
  T: "Under Construction",
  U: "Under Construction (<50%)",
  V: "Under Construction (>50%)",
  TS: "Construction Suspended",
};

export const PLANNED_STATUSES = new Set(["P", "L", "T", "U", "V", "TS"]);

export function isPlannedStatus(status: string | null): boolean {
  return status != null && PLANNED_STATUSES.has(status);
}

// ─── Status Filter Presets ──────────────────────────────────────────

export const STATUS_FILTER_PRESETS = [
  { label: "Operating", value: "OP" },
  { label: "Planned", value: "P,L,T,U,V,TS" },
  { label: "Under Construction", value: "T,U,V,TS" },
  { label: "All", value: "OP,SB,P,L,T,U,V,TS" },
] as const;

// ─── API Functions ───────────────────────────────────────────────────

export async function searchGenerators(
  params: GeneratorSearchParams = {}
): Promise<GeneratorSearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.ba_code) qs.set("ba_code", params.ba_code);
  if (params.state) qs.set("state", params.state);
  if (params.technology) qs.set("technology", params.technology);
  if (params.status) qs.set("status", params.status);
  if (params.min_capacity != null) qs.set("min_capacity", String(params.min_capacity));
  if (params.max_capacity != null) qs.set("max_capacity", String(params.max_capacity));
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));

  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetch(`${API}/public/generators${query}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getGeneratorDetail(
  plantId: string,
  generatorId: string
): Promise<GeneratorDetail> {
  const res = await fetch(
    `${API}/public/generators/${encodeURIComponent(plantId)}/${encodeURIComponent(generatorId)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function claimGenerator(
  plantId: string,
  generatorId: string,
  token: string
): Promise<GeneratorClaimResponse> {
  const res = await fetch(
    `${API}/public/generators/${encodeURIComponent(plantId)}/${encodeURIComponent(generatorId)}/claim`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getStatePipelineSummary(): Promise<StatePipelineSummary[]> {
  const res = await fetch(`${API}/public/generators/state-summary`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function registerUser(body: {
  email: string;
  password: string;
  full_name?: string;
  company_name?: string;
}): Promise<{ access_token: string; customer_id: string; user_id: string }> {
  const res = await fetch(`${API}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
